import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, Zap, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { crmFeatures, integrations } from '../data/mockData';
import {
  FeatureIcon1,
  FeatureIcon2,
  FeatureIcon3,
  FeatureIcon4,
  FeatureIcon5,
  FeatureIcon6,
  FeatureIcon7,
  FeatureIcon8,
  FeatureIcon9
} from '../components/Icons/Icons'
import Lightbox from '../components/Lightbox';

const BANNER_SLIDES = [
  { src: '/images/productsBanner3.webp', alt: 'GrowSquare dashboard with sales target, lead source, lead aging and engagement reports' },
  { src: '/images/productsBanner.webp', alt: 'GrowSquare leads list with lead score and status columns' },
  { src: '/images/productsBanner2.webp', alt: 'GrowSquare lead record with status pipeline, activity timeline and lead score' },
];

// Milliseconds each slide stays on screen. Slides change on this timer only (no scroll dependency).
const SLIDE_INTERVAL_MS = 2500;

// Image frame with tilted plates in the GrowSquare logo colours
const BannerSlideshow = () => {
  const [active, setActive] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [zoom, setZoom] = React.useState(false);
  React.useEffect(() => {
    if (paused || zoom) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % BANNER_SLIDES.length), SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, zoom]);
  return (
    <div
      className="relative mx-auto w-full max-w-[560px] lg:max-w-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative px-4 py-5">
        {/* tilted colour plates behind the image, using the logo colours */}
        <div
          className="absolute inset-0 rotate-[3deg] rounded-[28px] shadow-xl transition-transform duration-500"
          style={{ background: 'linear-gradient(135deg, #f9d628 0%, #4bb214 100%)' }}
        />
        <div
          className="absolute inset-0 rotate-[-2deg] rounded-[28px] opacity-95 transition-transform duration-500"
          style={{ background: 'linear-gradient(135deg, #f9617f 0%, #1fb6e8 100%)' }}
        />
        <div className="relative rounded-2xl border-[6px] border-white bg-white shadow-2xl">
          <div
            className="relative aspect-[5/4] cursor-zoom-in overflow-hidden rounded-xl bg-white"
            onClick={() => setZoom(true)}
            title="Click to view full size"
          >
            {BANNER_SLIDES.map((s, i) => {
              const n = BANNER_SLIDES.length;
              const off = (i - active + n) % n; // 0 = showing, 1.. = waiting on the right, last = just left
              const x = off === 0 ? 0 : off === n - 1 ? -100 : 100;
              return (
                <img
                  key={i}
                  src={s.src}
                  alt={s.alt}
                  loading="eager"
                  style={{ transform: `translateX(${x}%)` }}
                  className={`absolute inset-0 h-full w-full object-cover object-left-top transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${off === 0 ? 'opacity-100' : 'opacity-0'}`}
                />
              );
            })}
          </div>
        </div>
      </div>
      {zoom && <Lightbox src={BANNER_SLIDES[active].src} alt={BANNER_SLIDES[active].alt} onClose={() => setZoom(false)} />}
      <div className="mt-6 flex justify-center gap-2">
        {BANNER_SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show slide ${i + 1}`}
            onClick={() => setActive(i)}
            className={`h-2 rounded-full transition-all ${i === active ? 'w-6 bg-white' : 'w-2 bg-white/40'}`}
          />
        ))}
      </div>
    </div>
  );
};

const ProductsPage = () => {
  const getIcon = (iconName) => {
    const icons = {
      FeatureIcon1,
      FeatureIcon2,
      FeatureIcon3,
      FeatureIcon4,
      FeatureIcon5,
      FeatureIcon6,
      FeatureIcon7,
      FeatureIcon8,
      FeatureIcon9
    };
    return icons[iconName] || FeatureIcon1;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-[5fr_6fr] gap-12 items-center">
          <div className="animate-fade-in">
            <h1 className="display-lg text-white mb-6">
              GrowSquare: One CRM for Sales, Marketing and Service
            </h1>
            <p className="body-lg mb-8">
              Lead research, deal management, forecasting, marketing and customer service in one place. AI handles the repetitive work, such as scoring leads, drafting emails and flagging at-risk accounts, so your team spends more time with customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-start items-start sm:items-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              {/* <Link to="/products/1">
                <Button className="btn-secondary">
                  See AI in Action
                </Button>
              </Link> */}
            </div>
          </div>
          <BannerSlideshow />
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="bg-green-400/10 text-green-400 mb-4">Product Features</Badge>
            <h2 className="display-md text-white mb-6">
              What Is Included
            </h2>
            {/* <p className="body-lg max-w-3xl mx-auto">
              GrowSquare isn't just another CRM - it's an intelligent sales acceleration platform that learns
              from your data and automatically optimizes your sales process for maximum revenue growth.
            </p> */}
          </div>

          <div className="card-grid">
            {crmFeatures.map((feature, i) => {
              const IconComponent = getIcon(feature.icon);
              return (
                <Card key={i} className="feature-card hover-card">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-green-400/10 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-green-400" />
                    </div>
                    <CardTitle className="h3 text-white">{feature.title}</CardTitle>
                    <CardDescription className="body-md">
                      {feature.subTitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      {feature.benefits.slice(0, 3).map((benefit, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                          <span className="body-sm text-slate-300">{benefit}</span>
                        </div>
                      ))}
                    </div>
                    <Link to={`/products/${feature.title}`}>
                      <Button className="btn-secondary w-full">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Benefits */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-md text-white mb-6">
                How GrowSquare Differs from a Traditional CRM
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">AI Built into the Workflow</h3>
                    <p className="body-md text-slate-300">
                      Scoring, email drafting, forecasting and churn alerts work inside the screens your team already uses. There is no separate AI tool to set up or log in to.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">Less Setup Work</h3>
                    <p className="body-md text-slate-300">
                      Standard modules, assignment rules and approval flows come pre-built. We adjust them to your sales process during onboarding instead of starting from a blank configuration.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">Forecasts You Can Explain</h3>
                    <p className="body-md text-slate-300">
                      Forecasts combine deal stage, deal history and rep commitments, so managers can see why a number moved and what to do about it.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-green-400/20 to-transparent rounded-2xl p-8">
                <div className="h-full bg-slate-800 rounded-xl p-8 flex flex-col justify-center">
                  <div className="text-center space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700 rounded-lg p-4">
                        <div className="h2 text-green-400">2–4 weeks</div>
                        <p className="body-sm">Typical go-live for a standard setup</p>
                      </div>
                      <div className="bg-slate-700 rounded-lg p-4">
                        <div className="h2 text-green-400">10–20%</div>
                        <p className="body-sm">Less time on data entry and admin</p>
                      </div>
                      <div className="bg-slate-700 rounded-lg p-4">
                        <div className="h2 text-green-400">5–10 pts</div>
                        <p className="body-sm">Better forecast accuracy after a few quarters of use</p>
                      </div>
                      <div className="bg-slate-700 rounded-lg p-4">
                        <div className="h2 text-green-400">AI research</div>
                        <p className="body-sm">Contextual, deeper insights on accounts and contacts</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Integrations
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              GrowSquare connects to the tools your team already uses through secure APIs. We confirm which integrations you need during scoping and set them up as part of onboarding.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {integrations.map((category, i) => (
              <Card key={i} className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">{category.name}</CardTitle>
                  <CardDescription className="body-md">
                    {category.features.join(", ")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="body-sm text-slate-400">{category.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
             See GrowSquare with Your Own Data
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Start a free trial, or book a demo and we will walk through your sales process in the product.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout?plan=Trial&billing=monthly">
                {/* <Link to="/contact"> */}
                <Button className="btn-primary glow-effect">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Schedule Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default ProductsPage;